import {
  Component,
  PuzzleDropDownButton,
  Position,
  PuzzleButton,
  PuzzleCheckbox,
  PuzzleToolbar, Container, withDynamicSize
} from "puzzle";
import React, { ComponentType } from "react";
import KendoGridAbstract from "../../../util/KendoGridAbstract/KendoGridAbstract";
import {
  GridCellProps,
  GridColumn,
  GridColumnMenuProps,
  GridNoRecords,
  GridSelectionChangeEvent,
  GridSortChangeEvent
} from "@progress/kendo-react-grid";
import { getTableTdAccessibilityProps, popupAlignment } from "../../../util/Util";
import * as _ from "lodash";
import {
  COLLATERAL_FILES,
  RESPONSE_DATA, SUPPLEMENTARY_FILES_PROPS,
  SUPPLEMENTARY_FILES_STATE,
  SUPPLEMENTARY_HEADER_MENU
} from "./Types";
import TextComponent from "puzzle/components/UI/text_component/TextComponent";
import PoliciesManager from "../../../util/Policies";
import ColumnMenu from "puzzle/components/grid/columnmenu/ColumnMenu";
import { CheckboxChangeEvent } from "@progress/kendo-react-inputs";
import { DropDownButtonItemClickEvent } from "@progress/kendo-react-buttons";

export class SupplementaryFiles extends Component<SUPPLEMENTARY_FILES_PROPS, SUPPLEMENTARY_FILES_STATE> {
  selectedField = 'selected';
  dropdownItems = [
    { name: 'dropdownItems.selectedFiles' },
    { name: 'dropdownItems.allFiles' }
  ];
  state: SUPPLEMENTARY_FILES_STATE = {
    data: [],
    filesData: [],
    headerSelection: false,
    urlPrefix: this.props.isSupplierView ? 'jobPhases' : 'submissions',
    languages: [],
    selectedFiles: [],
    sort: [{
      field: 'dateAdded',
      dir: 'desc'
    }],
    uploadFiles: []
  }

  // @ts-ignore
  filesInput: React.Ref = React.createRef();

  moduleDidMount = () => {
    this.setDynamicSize();
    window.addEventListener('resize', () => {
      this.dispatch('handleDrawerResize', { buffer: 300 });
    });
    this.requestSupplementaryData('');
  }
  componentDidUpdate = (prevProps: any) => {
    const { data } = this.props
    const { submissionId } = data
    if (prevProps.data.submissionId !== submissionId) {
      this.requestSupplementaryData(Position.end);
      this.setState({
        selectedFiles: []
      })
    }
  }

  /**
   * Sets class data.
   * @param responseData
   */
  setData = (responseData: RESPONSE_DATA) => {
    const { collateralFiles, languages } = responseData
    this.filesInput.value = ''; //Resets value of the upload input field to be able to fire the change event on the same file upload
    let filesData = collateralFiles.map((file: any) => {
      return { ...file, selected: false }
    });
    this.setState({
      ...this.state,
      filesData,
      headerSelection: false,
      languages,
      selectedFiles: []
    })
  }

  /**
   * Request supplementary files data once the tab is opened
   */
  requestSupplementaryData = (position: string) => {
    const { data } = this.props
    const { urlPrefix } = this.state
    let url = `/${urlPrefix}/${data.submissionId}/supplementaryFiles`;
    this.dispatch('getSupplementaryFilesData', position, {
      submissionId: data.submissionId,
      url
    });
  }

  /**
   * Calculates height of the grid with function from HOC
   */
  setDynamicSize = () => this.props.setDynamicSize();

  /**
   * File selection handler.
   * @param item
   * @param event
   */
  selectionChange = (event: GridSelectionChangeEvent) => {
    let currentDataItem = event.dataItem;
    let { filesData } = this.state;

    let item: any = _.find(filesData, { repositoryTicket: currentDataItem.repositoryTicket });

    item.selected = !item.selected;
    let selectedFiles;
    if (item.selected) {
      selectedFiles = [...this.state.selectedFiles, item];
    } else {
      selectedFiles = this.state.selectedFiles.filter((filterItem: any) => item.repositoryTicket !== filterItem.repositoryTicket);
    }
    const headerSelection = filesData.every(item => item.selected);
    this.setState({
      ...this.state,
      filesData,
      headerSelection,
      selectedFiles
    })
  }

  /**
   * Grid cell render
   * @param data
   * @param cellProps
   */
  cellRender = (data: any, cellProps: GridCellProps) => {
    const { dataItem, field, selectionChange } = cellProps;
    const { dateAdded } = dataItem
    const tdProps = getTableTdAccessibilityProps(cellProps);
    let editedProps = _.cloneDeep(cellProps)
    let child;
    let sortableClass = "tp-sortable";
    switch (field) {
      case 'checked':
        child = ''
        editedProps.className = "tp-select-cell";
        child = <PuzzleCheckbox
          checked={dataItem[this.selectedField]}
          onChange={selectionChange}
        />;
        break
      case 'fileName':
        child = dataItem.fileName
        break
      case 'userName':
        child = dataItem.userName
        break
      case 'dateAdded':
        child = dateAdded ? this.translateByNamespace('date.dateWithTime', { date: new Date(dateAdded) }) : '';
        break
      case 'language':
        child = dataItem.language ? dataItem.language.value : this.translate("submission")
        break
    }

    return <td
      className={sortableClass}
      {...tdProps}
    >
      {child}
    </td>

  }

  /**
   * Sort change for grid columns
   */
  onSortChange = (e: GridSortChangeEvent) => {
    this.setState({
      ...this.state,
      sort: e.sort
    })
  }

  fileUploadHandler = () => {
    this.filesInput.click();
  };

  handleUpload = (e: any) => {
    const { files } = e.currentTarget;
    this.beforeUploadStart(files);
  }

  /**
   * Opens upload dialog
   * @param files
   */
  beforeUploadStart = (files: File[]) => {
    const { userType, data } = this.props;
    const { urlPrefix } = this.state;

    let uploadPrefix = userType === 'ORGANIZATION' ? 'submissions' : 'jobPhases';
    let dataId = userType === 'ORGANIZATION' ? data.uploadSubmissionId : data.submissionId;
    let urlLoadData = `/${urlPrefix}/${data.submissionId}/supplementaryFiles`;
    let urlUpload = `/${uploadPrefix}/${dataId}/supplementaryFiles/save`;
    this.dispatch('openSupplementaryUploadDialog', '', {
      urlLoadData,
      userType,
      urlUpload,
      filesToUpload: files,
      languages: this.state.languages,
      submissionData: data
    })
  }

  /**
   * Column menu render
   * @param props
   */
  renderColumnMenu = (props: GridColumnMenuProps): JSX.Element => {
    const columnName: string = props.column.field as string;
    let columnMenuProps: SUPPLEMENTARY_HEADER_MENU = {
      closeMenu: props.onCloseMenu as Function,
      sortable: true,
      onSortChange: this.onSortChange,
      filterable: false
    };

    return <ColumnMenu
      column={{ field: columnName }}
      {...columnMenuProps}
    />
  };

  /**
   * Renders dropdown items
   * @param type
   * @param propsItem
   */
  renderDropDown = (propsItem: any) => {
    const { selectedFiles } = this.state
    return propsItem.map((item: any, index: any) => {
      return {
        disabled: index === 0 && !selectedFiles.length,
        name: item.name,
        index
      }
    })
  }


  /**
   * Renders items for Delete and Download dropdowns
   * @param propsItem
   */
  itemRender = (propsItem: any) => {
    const { selectedFiles, filesData } = this.state;
    const { item } = propsItem;
    const count = item.index === 0 ? selectedFiles.length : filesData.length;
    return <div>
      {this.translate(item.name, { count })}
    </div>
  }

  /**
   * handler for clicking on select all/deselect all checkbox in header
   * * @param event
   */
  headerSelectionChange = (event: CheckboxChangeEvent) => {
    const checked = event.nativeEvent.target.checked;
    const { filesData = [] } = this.state;
    const newData = filesData.map((item: COLLATERAL_FILES) => {
      item.selected = checked;
      return item;
    });
    let selectedFiles: COLLATERAL_FILES[] = [];
    if (checked) {
      selectedFiles = [...newData];
    } else {
      selectedFiles = [];
    }
    this.setState({ data: newData, headerSelection: checked, selectedFiles });
  };

  /**
   * Handles dropdown item click
   * @param type
   * @param dropDownItem
   */
  onItemClick = (type: string, dropDownItem: DropDownButtonItemClickEvent,) => {
    const { filesData, selectedFiles, urlPrefix } = this.state
    const { data, isSupplierView } = this.props
    const { submissionId, submissionName } = data
    const { uploadSubmissionId } = data;

    let urlLoadData = `/${urlPrefix}/${submissionId}/supplementaryFiles`;
    let urlDelete = `${urlLoadData}/delete`;
    let paramsSupplementaryFiles = { downloadType: 'supplementary' };
    const submissionData = {
      taskId: submissionId,
      submissionName
    }
    const downloadPayloadKey = isSupplierView ? 'taskId' : 'submissionId';
    const downloadPayloadCommand = {
      [downloadPayloadKey]: submissionId
    }
    let resourceIds: string[] = [];
    selectedFiles.forEach((file: any) => {
      resourceIds.push(file.repositoryTicket)
    })

    let downloadAction = isSupplierView ? 'downloadFiles' : 'pingUntilDownloadIsReady';
    switch (type) {
      case 'delete':
        if (dropDownItem.itemIndex === 0) {
          this.dispatch('deleteSupplementaryFilesDialog', "", {
            fileCount: selectedFiles.length,
            urlDelete,
            urlLoadData,
            repositoryTickets: { repositoryTickets: resourceIds },
            submissionId,
          });
        } else {
          filesData.forEach((file: any) => {
            resourceIds.push(file.repositoryTicket)
          });
          this.dispatch('deleteSupplementaryFilesDialog', "", {
            fileCount: filesData.length,
            urlDelete,
            urlLoadData,
            repositoryTickets: { repositoryTickets: resourceIds },
            submissionId,
          });
        }
        break;
      case 'download':
        if (dropDownItem.itemIndex === 0) {
          let downloadParams;
          if (selectedFiles.length === 1) {
            downloadAction = 'downloadResources';
            downloadParams = {
              repositoryTicket: selectedFiles[0].repositoryTicket,
              category: 'supplementary',
              submissionId: isSupplierView ? uploadSubmissionId : submissionId
            };
          } else {
            downloadParams = { ...paramsSupplementaryFiles, resourceIds, downloadPayloadCommand };
          }
          this.dispatch(downloadAction, '', {
            submissionData,
            submissionId,
            params: downloadParams,
            afterDownloadAction: 'refreshApp'
          });
        } else {
          const downloadParams = { ...paramsSupplementaryFiles, resourceIds, downloadPayloadCommand };
          this.dispatch(downloadAction, '', {
            submissionData,
            submissionId,
            params: downloadParams,
            downloadPayloadCommand
          });
        }
        break;
      default:
        break;
    }
  }

  /**
   * Converts array rejectedFiles to proper object that can fit handleRejectionCode method on DropUpload class
   *
   * @param rejectedFiles
   */
  updateRejectedFiles = ({ rejectedFiles }: any) => {
    return {
      rejected: rejectedFiles
    }
  };

  /**
   * Custom selected column header cell render
   */
  selectedHeaderColumnCellRender = () => {
    const { headerSelection } = this.state;
    return (
      <PuzzleCheckbox
        checked={headerSelection}
        className={'tp-drawer-files-grid-header-select-cell'}
        onChange={this.headerSelectionChange}
        aria-label={this.translate("SRL-checkboxHeaderLabel")}
      />
    )
  };

  /**
   * Returns the field which file is sorted by
   */
  returnSortingField = () => {
    const { sort } = this.state;
    if (sort[0].field !== 'language') {
      return sort[0].field;
    } else {
      return 'language.value';
    }
  };

  renderBody() {
    const {
      filesData,
      sort
    } = this.state;

    const {
      taskStatus,
      submissionStatus
    } = this.props.data;

    const columnMenu: {
      className: string,
      columnMenu: ComponentType<GridColumnMenuProps>,
      headerClassName: string,
      minResizableWidth: number
    } = {
      className: 'tp-sortable',
      columnMenu: this.renderColumnMenu,
      headerClassName: 'tp-sortable',
      minResizableWidth: 120
    };

    const enableUploadAndDelete = !_.includes(['PROCESSING', 'IN_ERROR', 'CANCELLED'], submissionStatus?.name);
    const sortingField = this.returnSortingField();
    const sortedData = _.orderBy(filesData, sortingField, sort[0].dir);
    return (
      <Container className={'tp-drawer-body-tab-content'}>
        <span className={'tp-drag-and-drop-wrapper'}>
          <input
            style={{ display: 'none' }}
            type="file"
            ref={(ref) => this.filesInput = ref}
            className="tp-file-input"
            multiple
            id={'inputId'}
            onChange={this.handleUpload}
          />
        </span>
        <PuzzleToolbar
          className={'tp-topbar-actions'}
        >
          {
            !filesData.length || (_.includes(['COMPLETED', 'CANCELLED'], taskStatus?.name)) ? null :
              <>{
                (enableUploadAndDelete &&
                  PoliciesManager.hasDeleteDeliverableCollateral) &&
                <PuzzleDropDownButton
                  icon={'arrow-s'}
                  themeColor={'dark'}
                  reverse
                  popupSettings={popupAlignment('tp-custom-dropdown-list tp-download-list')}
                  items={this.renderDropDown(PoliciesManager.hasDeleteAllDeliverableCollateral ? this.dropdownItems : this.dropdownItems.slice(0, 1))}
                  onItemClick={this.onItemClick.bind(this, 'delete')}
                  text={this.translate('delete')}
                  itemRender={this.itemRender}
                />
              }
                <PuzzleDropDownButton
                  icon={'arrow-s'}
                  themeColor={'dark'}
                  reverse
                  popupSettings={popupAlignment('tp-custom-dropdown-list tp-download-list')}
                  items={this.renderDropDown(this.dropdownItems)}
                  onItemClick={this.onItemClick.bind(this, 'download')}
                  text={this.translate('download')}
                  itemRender={this.itemRender}
                />
                <div className={'tp-separator'} />
                {enableUploadAndDelete &&
                  <PuzzleButton
                    themeColor={'primary'}
                    onClick={this.fileUploadHandler}
                  >
                    {this.translate('upload')}
                  </PuzzleButton>
                }
              </>
          }
        </PuzzleToolbar>

        <div
          className={'tp-drawer-body-tab-files-content tp-main-grid-wrap'}
          ref={this.props.innerRef}
        >
          <KendoGridAbstract
            captionText={this.translate("SRL-suppFilesTableCaptionText")}
            className={'tp-main-grid tp-drawer-supplementary-tab-grid'}
            data={sortedData}
            dragAndDropEnabled={enableUploadAndDelete}
            dNdMessage={this.translate('dragAndDropText')}
            total={5}
            cellRender={this.cellRender}
            sort={sort}
            onSelectionChange={this.selectionChange}
            selectedField={this.selectedField}
            sortable={{ allowUnsort: false }}
            uploadFiles={this.beforeUploadStart}
            resizable
          >
            <GridNoRecords>
              <div className='k-vbox k-justify-content-center k-align-items-center'>
                <div className={`tp-grid-empty-state-img no-documents`} />
                <TextComponent className='k-text-center tp-text-semibold' size={'xl'}>
                  {this.translate('gridEmptyLabel')}
                </TextComponent>
                {_.includes(['COMPLETED', 'CANCELLED'], taskStatus?.name) ? null :
                  enableUploadAndDelete &&
                  <PuzzleButton
                    themeColor={'primary'}
                    onClick={this.fileUploadHandler}
                  >
                    {this.translate('upload')}
                  </PuzzleButton>
                }
              </div>
            </GridNoRecords>
            <GridColumn
              field='checked'
              headerClassName={'tp-border-none'}
              headerCell={this.selectedHeaderColumnCellRender}
              title={' '}
              resizable={false}
              width={'50px'}
            />
            <GridColumn
              field="fileName"
              title={this.translate('columnHeaders.fileName')}
              {...columnMenu}
            />
            <GridColumn
              field="userName"
              title={this.translate('columnHeaders.addedBy')}
              {...columnMenu}
            />
            <GridColumn
              field="dateAdded"
              title={this.translate('columnHeaders.dateAdded')}
              {...columnMenu}
            />
            <GridColumn
              field="language"
              title={this.translate('columnHeaders.level')}
              {...columnMenu}
            />
          </KendoGridAbstract>
        </div>
      </Container>
    )
  }

}

export default withDynamicSize(SupplementaryFiles);
